import { useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { EditableCell } from "@/components/EditableCell";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { cellAnswerRequest } from "@/lib/cellAnswer";
import { usePlannerLens } from "@/hooks/usePlannerLens";
import { fieldKeyOf } from "@/lib/planner-lens-ui";
import { isTypedRowSection } from "@shared/constants/typed-sections";

/** A typed record's row id (a seeded scaffold row is not a record, s228). */
const UUID_ROW = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
import { gapSentence } from "@shared/constants/consequence-copy";
import { deriveCellInputType } from "@shared/constants/section-layout-config";
import type { GenericField } from "@shared/schema";
import type { LensTier, PlannerGap, PlannerRecordGap } from "@shared/planner-lens";
import { saveSettled, useSaveFailure } from "@/components/interview/saveFailure";
import { HOME_BOARD, chapterTitle, fill, screenForGap } from "./planBoard";
import { CA_PROVINCES } from "@shared/constants/provinces";
import { formatPhone } from "@shared/phone";
import type { AnswerRow, OwnListRow, OwnState } from "@shared/plan-home/own-list";
import { useOwnList, ownListKey } from "./useOwnList";
import { NO_EXECUTOR_YET, useExecutorFirstName } from "./useExecutorFirstName";
import { useQuery } from "@tanstack/react-query";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { PickerCell } from "@/components/interview/InterviewCells";
import { LocationBox, LocationBoxPanel } from "@/components/interview/LocationBox";
import { ContactCard, type CardSlot } from "@/components/interview/PersonPicker";
import { SECTION_SLUGS } from "@shared/constants/sections";
import { FIELD_KEYS } from "@shared/constants/fields";
import { EXECUTOR_ROLE } from "@shared/constants/executor-roles";
import { AddressInput } from "@/components/AddressInput";
import { formatAddressSummary } from "@shared/address";

/** s233 (D-233.74; board 12 v433) — the ONE answer column: every row, whatever it asks, puts its box in column 2. */
const ROW_GRID = "grid min-h-[60px] grid-cols-1 items-center gap-x-4 gap-y-2 min-[1180px]:grid-cols-[minmax(0,1fr)_380px_150px]";
/** The closed box an address or a person shows: one line, the answer or its placeholder; clicking opens it below. */
const OPEN_BOX = "flex h-11 w-full min-w-0 items-center rounded-[8px] border border-warm-stone bg-warm-white px-3 text-left text-[14px]";
/** The address box's empty word — the interview's address control's own (AddressInput, the street box). */
const ADDRESS_PH = "Street address";

/**
 * s233 (D-233.74) — the contact card a PERSON answer opens: the name field, and the phone and email beside it when the
 * section has them (`<who>_phone`, `<who>_email` for `<who>_name`). A `contact` field links a contact; a text name
 * keeps the words.
 */
function personSlot(label: string, key: string, fieldType: string, keys: ReadonlySet<string>): CardSlot {
  const who = key.replace(/_name$/, "");
  const phone = `${who}_phone`, email = `${who}_email`;
  return {
    label,
    name: fieldType === "contact" ? { kind: "contact", key } : { kind: "text", key },
    ...(keys.has(phone) ? { phone } : {}),
    ...(keys.has(email) ? { email } : {}),
  };
}

/**
 * s233 (D-233.37 point 3; board 12 v433 `isLong` / `isCard`) — the answer box a question gets on the home, by the field
 * it asks: the long-text panel for a place (the will's original, the car's papers), a contact card for the accountant.
 * Every other question keeps the inline box. `words` are the board's (a new field's label and why, where the board draws them).
 */
type LongBox = { kind: "long"; title: string; hint: string; ph?: string; words?: { label: string; why: string } };
type CardBox = { kind: "card"; cardKind: string; slot: CardSlot };
const BOX: Readonly<Record<string, LongBox | CardBox>> = {
  [`${SECTION_SLUGS.LEGAL_IMPORTANT_DOCUMENTS}|${FIELD_KEYS.LOCATION}`]: { kind: "long", ...HOME_BOARD.answers.willPlace },
  [`${SECTION_SLUGS.ASSETS_VEHICLE_LIST}|${FIELD_KEYS.PAPERS_LOCATION}`]: {
    kind: "long", title: HOME_BOARD.answers.carPapers.title, hint: HOME_BOARD.answers.carPapers.hint, ph: HOME_BOARD.answers.carPapers.ph,
    words: { label: HOME_BOARD.answers.carPapers.label, why: HOME_BOARD.answers.carPapers.why },
  },
  [`${SECTION_SLUGS.FINANCIAL_TAXES}|${FIELD_KEYS.ACCOUNTANT_NAME}`]: {
    kind: "card", cardKind: HOME_BOARD.answers.accountant.cardKind,
    slot: { label: HOME_BOARD.answers.accountant.cardKind, name: { kind: "text", key: FIELD_KEYS.ACCOUNTANT_NAME }, phone: FIELD_KEYS.ACCOUNTANT_PHONE, email: FIELD_KEYS.ACCOUNTANT_EMAIL, org: true },
  },
};

/** s233 (D-233.37 point 2): a group heading — 14px, weight 500, dark green, a line under it (board 12 v433). */
function GroupHeading({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1.5 border-b border-warm-stone pb-1.5 pt-3.5 text-[14px] font-medium uppercase tracking-[0.06em] text-sage-800">
      {children}
    </span>
  );
}

/**
 * PLAN-HOME-1 (s228) — "WHAT YOUR EXECUTOR STILL NEEDS" on the plan's home (board 12, Home.dc.html:519-564;
 * rule 127(6)): full width, grouped BY WHERE YOU GO (the chapter and screen that asks it), an answer box on every
 * row that saves as you go, the costliest first.
 *
 *  • The gaps are the planner lens's — the same list production's `ExecutorNeedsPanel` shows — re-grouped: a gap
 *    goes to the ONE interview screen that asks its field (`screenForGap`); a gap no built screen asks is grouped
 *    by its section and answered in place, with nothing to open.
 *  • Its own counters are gone: the counter lives on the Executor tab only (rule 127(2)).
 *  • The first three rows show; "All N" opens the rest IN PLACE, answer boxes working (Reade, ruling C; the full
 *    page was deleted, D-199.14). N is the number of rows the list holds — the board wrote tasks-less-ready, which
 *    counts tasks, not the answers listed here (reported s228).
 *  • A saved row stays for this visit, marked Saved; the counter on the tab moves.
 */

const SHOWN = 3;

/**
 * s228 (Reade, staging walk item 6c): a field's name as the HOME shows it — sentence case ("Date of birth", "Mailing
 * address"), and "Relationship to Deceased" reads "Relationship to you" (the planner is alive, reading their own plan).
 * An all-capitals word (SIN, CPP) keeps its capitals. Pure; the field's stored name is unchanged.
 */
export function homeLabel(label: string): string {
  if (/^relationship to (the )?deceased$/i.test(label.trim())) return "Relationship to you";
  const sentence = label.trim().split(/\s+/).map((w, i) => {
    if (i === 0) return w.charAt(0).toUpperCase() + w.slice(1);
    return /^[A-Z0-9]{2,}$/.test(w) ? w : w.toLowerCase();
  }).join(" ");
  // D-228.67 (Reade): proper nouns keep their capitals — programmes, agencies, places. Longest first, so
  // "Canada Pension Plan" is restored before "Canada".
  return PROPER_NOUNS.reduce((s, name) => s.replace(new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "gi"), name), sentence);
}

/**
 * D-228.67 — the names `homeLabel` never lowercases. Programmes and agencies are listed here; provinces and territories
 * come from the product's one list (`CA_PROVINCES`), never typed again.
 */
export const PROPER_NOUNS: readonly string[] = [
  "Canada Pension Plan", "Old Age Security", "Guaranteed Income Supplement", "Canada Revenue Agency", "Service Canada",
  "ServiceOntario", "Veterans Affairs Canada", "Employment Insurance", "Canada Child Benefit", "Registered Retirement Savings Plan",
  "Registered Retirement Income Fund", "Tax-Free Savings Account", "Registered Education Savings Plan", "Registered Disability Savings Plan",
  ...CA_PROVINCES.map((p) => p.name), "Canada",
].sort((a, b) => b.length - a.length);
const TIER_RANK: Record<LensTier, number> = { critical: 0, important: 1, recommended: 2, informational: 3 } as Record<LensTier, number>;

type Row = { key: string; rec: PlannerRecordGap; gap: PlannerGap; group: string; groupName: string; open?: string };

/** s232 (PR 2): the hand-overs' group — its own card since s233 (board 12 v433). */
const HANDOVERS = "Hand-overs";

export function HomeNeeds({ clientId, onOpen }: { clientId: string; onOpen: (screenKey: string) => void }) {
  const { data } = usePlannerLens(clientId);
  const { data: own } = useOwnList(clientId);
  const executor = useExecutorFirstName(clientId) || NO_EXECUTOR_YET;
  const [all, setAll] = useState(false);
  const saved = useRef(new Map<string, Row>());
  const [, bump] = useState(0);

  const rows = useMemo(() => {
    const live: Row[] = (data?.records ?? []).flatMap((rec) =>
      rec.fields.map((gap) => {
        const at = screenForGap(rec.sectionSlug, fieldKeyOf(gap.ref));
        return {
          key: `${rec.sectionSlug}|${rec.rowId ?? ""}|${gap.ref}`,
          rec,
          gap,
          group: at ? at.key : `section:${rec.sectionSlug}`,
          groupName: at ? `${chapterTitle(at.chapter)} · ${at.crumb}` : rec.sectionName,
          open: at?.key,
        };
      }),
    );
    live.sort((a, b) => a.gap.stage - b.gap.stage || (TIER_RANK[a.gap.tier] ?? 9) - (TIER_RANK[b.gap.tier] ?? 9));
    // A row answered on this visit stays, marked Saved, even after the lens drops it.
    const keys = new Set(live.map((r) => r.key));
    return [...live, ...Array.from(saved.current.values()).filter((r) => !keys.has(r.key))];
  }, [data]);

  const ownRows = Array.isArray(own?.rows) ? own.rows : [];
  const answers: AnswerRow[] = Array.isArray(own?.answers) ? own.answers : [];
  if (!data || (rows.length === 0 && ownRows.length === 0 && answers.length === 0)) return null;
  // s233: the answer rows are questions too — the subtitle's number counts them with the lens's.
  const open = rows.filter((r) => !saved.current.has(r.key)).length + answers.length;
  // s233 (D-233.37 point 5): every to-do and hand-over sits in the Hand-overs card, grouped as the board groups them.
  const handGroups: { name: string; rows: OwnListRow[] }[] = [];
  for (const r of ownRows) {
    const g = handGroups.find((x) => x.name === r.group) ?? (handGroups.push({ name: r.group, rows: [] }), handGroups[handGroups.length - 1]);
    g.rows.push(r);
  }
  const answerGroups: { name: string; rows: AnswerRow[] }[] = [];
  for (const a of answers) {
    const g = answerGroups.find((x) => x.name === a.group) ?? (answerGroups.push({ name: a.group, rows: [] }), answerGroups[answerGroups.length - 1]);
    g.rows.push(a);
  }
  const visible = all ? rows : rows.slice(0, SHOWN);
  const groups: { id: string; name: string; rows: Row[] }[] = [];
  for (const r of visible) {
    const g = groups.find((x) => x.id === r.group) ?? (groups.push({ id: r.group, name: r.groupName, rows: [] }), groups[groups.length - 1]);
    g.rows.push(r);
  }

  return (
    <>
    <section className="flex flex-col gap-1.5 rounded-[12px] border border-warm-stone bg-warm-white px-6 py-5" data-testid="home-needs">
      <div className="flex items-baseline justify-between gap-4">
        <div className="flex flex-col gap-[3px]">
          <h2 className="font-serif text-[22px] font-normal">{HOME_BOARD.needs.title}</h2>
          <span className="text-[13px] text-warm-muted" data-testid="home-needs-sub">
            {fill(HOME_BOARD.needs.sub, { n: open, dos: own?.counts?.open ?? 0, executor })}
          </span>
        </div>
        {!all && rows.length > SHOWN && (
          <button type="button" onClick={() => setAll(true)}
            className="flex min-h-11 items-center whitespace-nowrap rounded-[8px] px-3 text-[14px] text-sage-800 hover:bg-warm-stone-light"
            data-testid="home-needs-all">
            {fill(HOME_BOARD.needs.all, { n: rows.length })}
          </button>
        )}
      </div>
      {/* s233 (D-233.37 points 2–4): the answer rows lead — the will's questions, asked in place. */}
      {answerGroups.map((g) => (
        <div key={g.name} className="flex flex-col" data-testid={`home-answer-group-${slugOf(g.name)}`}>
          <GroupHeading>{g.name}</GroupHeading>
          {g.rows.map((a) => <AnswerRowView key={a.key} clientId={clientId} row={a} executor={executor} />)}
        </div>
      ))}
      {groups.map((g) => (
        <div key={g.id} className="flex flex-col" data-testid={`home-needs-group-${g.id}`}>
          <GroupHeading>{g.name}</GroupHeading>
          {g.rows.map((r) => (
            <NeedRow key={r.key} clientId={clientId} row={r} saved={saved.current.has(r.key)} executor={executor}
              onSaved={() => { saved.current.set(r.key, r); bump((n) => n + 1); }} onOpen={onOpen} />
          ))}
        </div>
      ))}
    </section>
    {/* s233 (D-233.37 point 5; board 12 v433): the hand-overs are their own card, below the questions. */}
    {handGroups.length > 0 && (
      <section className="flex flex-col gap-1.5 rounded-[12px] border border-warm-stone bg-[#F5F0E4] px-5 py-3" data-testid="home-handovers">
        {handGroups.map((g) => (
          <OwnGroup key={g.name} clientId={clientId} name={g.name} rows={g.rows} executor={executor} onOpen={onOpen}
            sub={g.name === HANDOVERS ? fill(HOME_BOARD.ownList.handoversSub, { executor }) : undefined} />
        ))}
      </section>
    )}
    </>
  );
}

const slugOf = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/** s233 (D-233.37 points 2 and 4) — an answer row: the will's "last updated" choice, or the backup's contact card. */
function AnswerRowView({ clientId, row, executor }: { clientId: string; row: AnswerRow; executor: string }) {
  const onSaveFailed = useSaveFailure();
  const [open, setOpen] = useState(false);
  const w = row.write;
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ownListKey(clientId) });
    void queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "planner-lens"] });
    void queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "generic-entries"] });
  };
  // The choice: the will row's cell upserts on (client, field, row), so a second fire lands on it — still ONE lock
  // (useSingleFlight), so a quick second pick waits for the first rather than racing it.
  const choiceFlight = useSingleFlight();
  const choiceMutation = useMutation({
    mutationFn: async (value: string) =>
      isTypedRowSection(SECTION_SLUGS.LEGAL_IMPORTANT_DOCUMENTS) && UUID_ROW.test(w.rowId ?? "")
        ? apiRequest("POST", `/api/clients/${clientId}/typed-rows`, {
            categoryId: w.categoryId, sectionId: w.sectionId, rowId: w.rowId, cells: [{ fieldId: w.fieldIds[FIELD_KEYS.LAST_UPDATED], value }],
          })
        : apiRequest("POST", `/api/clients/${clientId}/generic-entries`, {
            categoryId: w.categoryId, sectionId: w.sectionId, fieldId: w.fieldIds[FIELD_KEYS.LAST_UPDATED], rowId: w.rowId, value,
          }),
    onError: onSaveFailed,
  });
  // The backup: Class A — a second fire would be a second backup. ONE lock (useSingleFlight), released on the promise.
  const createFlight = useSingleFlight();
  const backupMutation = useMutation({
    mutationFn: async (changes: Record<string, string>) => {
      const cells = [
        { fieldId: w.fieldIds[FIELD_KEYS.ROLE], value: EXECUTOR_ROLE.ALTERNATE },
        ...Object.entries(changes).filter(([k, v]) => w.fieldIds[k] && v.trim() !== "").map(([k, v]) => ({ fieldId: w.fieldIds[k], value: v.trim() })),
      ];
      return apiRequest("POST", `/api/clients/${clientId}/typed-rows`, { categoryId: w.categoryId, sectionId: w.sectionId, rowId: crypto.randomUUID(), cells });
    },
    onError: onSaveFailed,
  });
  const tid = (what: string) => `home-answer-${what}-${row.key}`;
  const fieldIdByKey = new Map(Object.entries(w.fieldIds));
  return (
    <div className="border-t border-warm-stone-light py-2" data-testid={`home-answer-${row.key}`}>
      <div className={ROW_GRID}>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[15px]">{row.label}</span>
          <span className="text-[13px] text-warm-muted">{fill(row.why, { executor })}</span>
        </div>
        <div className="min-w-0" data-home-answer-box="">
          {row.box.kind === "choice" ? (
            <PickerCell value="" options={row.box.opts} placeholder={row.box.ph} ariaLabel={row.label} testId={tid("input")}
              onCommit={(v) => { void choiceFlight.run(() => saveSettled(choiceMutation.mutateAsync(v)).then((ok) => { if (ok) refresh(); })); }} />
          ) : (
            <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
              className="flex h-11 w-full items-center rounded-[8px] border border-warm-stone bg-warm-white px-3 text-left text-[14px] text-warm-muted"
              data-testid={tid("open")}>
              {row.box.ph}
            </button>
          )}
        </div>
        <div />
      </div>
      {row.box.kind === "card" && open && (
        <div data-testid={`home-answer-card-${row.key}`}>
          <ContactCard title={row.box.cardKind} clientId={clientId} values={{}} fieldIdByKey={fieldIdByKey}
            slots={[{ label: row.box.cardKind, name: { kind: "text", key: FIELD_KEYS.NAME }, phone: FIELD_KEYS.PHONE, email: FIELD_KEYS.EMAIL }]}
            undo={HOME_BOARD.answers.cancelWord} testId={`home-answer-card-${row.key}`}
            onSave={(changes) => {
              if (!(changes[FIELD_KEYS.NAME] ?? "").trim()) return;
              return createFlight.run(() => saveSettled(backupMutation.mutateAsync(changes)).then((ok) => { if (ok) { setOpen(false); refresh(); } }));
            }}
            onClose={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}

function NeedRow({ clientId, row, saved, onSaved, onOpen, executor }: {
  clientId: string; row: Row; saved: boolean; onSaved: () => void; onOpen: (screenKey: string) => void; executor: string;
}) {
  const { rec, gap } = row;
  const write = gap.write;
  const [draft, setDraft] = useState("");
  const draftRef = useRef("");
  const inFlight = useRef(false);
  const onSaveFailed = useSaveFailure();
  // s233 (D-233.37 point 3): the answer box this question gets — the long-text panel, a contact card, or the inline box.
  const fieldKey = fieldKeyOf(gap.ref);
  const box = BOX[`${rec.sectionSlug}|${fieldKey}`];
  // s233 (D-233.74): an address is one box that opens the address form below; every person answer opens the card.
  const address = !box && write?.fieldType === "address";
  const person = box?.kind === "card" || (!box && write?.fieldType === "contact");
  const [panelOpen, setPanelOpen] = useState(false);
  const [addressDraft, setAddressDraft] = useState("");
  // What was saved here this visit — the address box shows it on its one line.
  const [savedValue, setSavedValue] = useState("");

  // Class C-server-deduped: generic_entries upserts on (client, field, row); a second fire lands on the same row.
  // s229 (D-229.68(b)): a Person / Pet gap on a typed row saves as that typed record (the typed record and
  // its cells upsert by row, so a second fire lands on the same ones); every other gap saves its cell.
  // s233: the accountant's card writes more than one field — each by its own id, on the same row.
  const saveNeedMutation = useMutation({
    // s237 (rule 159): the one cell-answer request, shared with Where things are (lib/cellAnswer.ts).
    mutationFn: async ({ fieldId, value }: { fieldId: string; value: string }) => {
      const r = cellAnswerRequest(clientId, { sectionSlug: rec.sectionSlug, sectionId: write!.sectionId, categoryId: write!.categoryId, rowId: rec.rowId ?? null }, fieldId, value);
      return apiRequest("POST", r.url, r.body);
    },
    onError: onSaveFailed,
  });
  // The card needs its other fields' ids (the phone beside the accountant's name): the section's fields, read once.
  const { data: sectionFields } = useQuery<{ id: string; fieldKey: string }[]>({
    queryKey: ["/api/generic-sections", write?.sectionId, "fields"],
    queryFn: async () => (await apiRequest("GET", `/api/generic-sections/${write!.sectionId}/fields`)).json(),
    enabled: person && !!write,
  });
  const fieldList = Array.isArray(sectionFields) ? sectionFields : [];
  const slot: CardSlot | undefined = box?.kind === "card" ? box.slot
    : person ? personSlot(homeLabel(gap.label), fieldKey, write!.fieldType, new Set(fieldList.map((f) => f.fieldKey))) : undefined;
  const cardTitle = box?.kind === "card" ? box.cardKind : homeLabel(gap.label);

  const settle = (p: Promise<unknown>) =>
    saveSettled(p).then((ok) => {
      if (!ok) return false;
      onSaved();
      void queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "planner-lens"] });
      void queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "generic-entries"] });
      return true;
    });
  const saveValue = (raw: string) => {
    const value = raw.trim();
    if (!write || !value || inFlight.current || saved) return Promise.resolve(false);
    inFlight.current = true;
    // Refused: the pop-up has said why (saveFailure.ts) and what was typed stays in the box.
    return settle(saveNeedMutation.mutateAsync({ fieldId: write.fieldId, value })).finally(() => { inFlight.current = false; });
  };
  const save = () => { void saveValue(draftRef.current); };
  const saveCard = async (changes: Record<string, string>) => {
    const ids = new Map(fieldList.map((f) => [f.fieldKey, f.id]));
    ids.set(fieldKey, write!.fieldId);
    const writes = Object.entries(changes).filter(([k, v]) => ids.has(k) && v.trim() !== "");
    if (!writes.length) return;
    const all = Promise.all(writes.map(([k, v]) => saveNeedMutation.mutateAsync({ fieldId: ids.get(k)!, value: v.trim() })));
    if (await settle(all)) setPanelOpen(false);
  };
  // The address form's Save: one JSON value through the same write; a refused save keeps the form open with the draft.
  const saveAddress = async () => {
    if (!formatAddressSummary(addressDraft).trim()) { setPanelOpen(false); return; }
    if (await saveValue(addressDraft)) { setSavedValue(addressDraft); setPanelOpen(false); }
  };

  // s228 (item 6b): a record with no name yet says so, in the role's own word ("Unnamed beneficiary").
  const who = rec.recordLabel || (rec.rowId && rec.recordNoun ? `Unnamed ${rec.recordNoun}` : "");
  const why = box?.kind === "long" && box.words ? box.words.why : [who, gapSentence(gap)].filter(Boolean).join(" · ");
  const label = box?.kind === "long" && box.words ? box.words.label : homeLabel(gap.label);
  // s228 (item 1): three columns only where they fit (the label keeps its words); below that the answer drops under
  // the label. s233 (D-233.74): EVERY row — address and person too — uses the one grid, so every answer box starts at
  // the same left edge with the same width; a form that opens (address, card, long text) spans the row under it.
  const tid = `home-need-${gap.ref}`;
  const addressText = formatAddressSummary(savedValue);
  return (
    <div className={`${ROW_GRID} border-t border-warm-stone-light py-2`} data-testid={tid} data-home-row="">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[15px]" data-testid={`home-need-label-${gap.ref}`}>{label}</span>
          <span className="text-[13px] text-warm-muted" data-testid={`home-need-why-${gap.ref}`}>{why}</span>
        </div>
        <div className="min-w-0" data-home-answer-box="">
          {address && write ? (
            // One box, one line: the address saved here, or the control's own empty word. Opens the form below.
            <button type="button" onClick={() => { if (!saved) setPanelOpen((o) => !o); }} aria-expanded={panelOpen} title={addressText || undefined}
              className={`${OPEN_BOX} ${addressText ? "text-warm-charcoal" : "text-warm-muted"}`} data-testid={`home-need-address-${gap.ref}`}>
              <span className="min-w-0 flex-1 truncate">{addressText || ADDRESS_PH}</span>
              {saved && <Check className="ml-2 h-3.5 w-3.5 shrink-0 text-sage-600" aria-label="Saved" data-testid={`home-need-saved-${gap.ref}`} />}
            </button>
          ) : saved ? (
            <span className="flex min-h-[46px] items-center gap-1 text-[13px] text-sage-600" data-testid={`home-need-saved-${gap.ref}`}>
              <Check className="h-3.5 w-3.5" aria-hidden="true" /> Saved
            </span>
          ) : !write ? null : box?.kind === "long" ? (
            <LocationBox value="" placeholder={box.ph}
              onCommit={(v) => saveValue(v)} open={panelOpen} onOpen={() => setPanelOpen((o) => !o)} testId={`home-need-long-${gap.ref}`} />
          ) : person ? (
            <button type="button" onClick={() => setPanelOpen((o) => !o)} aria-expanded={panelOpen}
              className={`${OPEN_BOX} text-warm-muted`} data-testid={`home-need-card-${gap.ref}-open`}>
              <span className="min-w-0 flex-1 truncate">{cardTitle}</span>
            </button>
          ) : (
            <EditableCell
              className="w-full min-w-0"
              value={draft}
              onChange={(v) => { draftRef.current = v; setDraft(v); }}
              onBlur={() => {
                // s228 (item 69): a phone answer takes the product's one convention before it is saved.
                if (write.fieldType === "phone") { const f = formatPhone(draftRef.current); draftRef.current = f; setDraft(f); }
                save();
              }}
              type={deriveCellInputType(write.fieldType)}
              options={write.options ?? undefined}
              showBorder
              ariaLabel={gap.label}
              testId={`home-need-input-${gap.ref}`}
              field={{ id: write.fieldId, name: gap.label, fieldKey, fieldType: write.fieldType as GenericField["fieldType"] }}
            />
          )}
        </div>
        <div className="flex min-[1180px]:justify-end">
          {row.open && (
            <button type="button" onClick={() => onOpen(row.open!)}
              className="flex min-h-11 items-center whitespace-nowrap rounded-[8px] px-2 text-[13.5px] text-sage-600 hover:bg-warm-stone-light"
              data-testid={`home-need-open-${gap.ref}`}>
              {HOME_BOARD.needs.act}
            </button>
          )}
        </div>
      {!saved && write && address && panelOpen && (
        // The interview's own address control, under the row (the long-text panel's place and width).
        <div className="col-span-full mb-3 flex max-w-[760px] flex-col gap-2.5 rounded-[8px] border border-warm-stone bg-warm-white p-4"
          data-testid={`home-need-address-${gap.ref}-panel`}>
          <span className="text-[15px] text-warm-charcoal">{label}</span>
          <AddressInput value={addressDraft} onChange={setAddressDraft} onBlur={() => {}} fieldId={write.fieldId} />
          <div className="flex items-center gap-4">
            <button type="button" onClick={() => { void saveAddress(); }}
              className="h-11 rounded-[8px] bg-sage-600 px-5 text-[14px] text-warm-white hover:bg-sage-800"
              data-testid={`home-need-address-${gap.ref}-save`}>
              {HOME_BOARD.answers.saveWord}
            </button>
            <button type="button" onClick={() => setPanelOpen(false)} className="text-[14px] text-warm-muted hover:text-warm-charcoal"
              data-testid={`home-need-address-${gap.ref}-cancel`}>
              {HOME_BOARD.answers.cancelWord}
            </button>
          </div>
        </div>
      )}
      {!saved && write && box?.kind === "long" && panelOpen && (
        <div className="col-span-full">
          <LocationBoxPanel value="" title={box.title} hint={fill(box.hint, { executor })}
            onCommit={(v) => saveValue(v)} onClose={() => setPanelOpen(false)} testId={`home-need-long-${gap.ref}`} />
        </div>
      )}
      {!saved && write && slot && panelOpen && (
        <div className="col-span-full" data-testid={`home-need-card-${gap.ref}`}>
          <ContactCard title={cardTitle} clientId={clientId} values={{}} slots={[slot]}
            fieldIdByKey={new Map([...fieldList.map((f) => [f.fieldKey, f.id] as const), [fieldKey, write.fieldId] as const])}
            undo={HOME_BOARD.answers.cancelWord} testId={`home-need-card-${gap.ref}`}
            onSave={saveCard} onClose={() => setPanelOpen(false)} />
        </div>
      )}
    </div>
  );
}

/** s232 (PR 2): a group of the ONE LIST's rows — the board's words, the plan's `{executor}`. */
function OwnGroup({ clientId, name, rows, executor, sub, onOpen }: {
  clientId: string; name: string; rows: OwnListRow[]; executor: string; sub?: string; onOpen: (key: string) => void;
}) {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return (
    <div className="flex flex-col" data-testid={`home-own-group-${slug}`}>
      <GroupHeading>{name}</GroupHeading>
      {sub &&<span className="pb-1 text-[13px] text-warm-muted" data-testid={`home-own-group-sub-${slug}`}>{sub}</span>}
      {rows.map((r) => <OwnRow key={r.id} clientId={clientId} row={r} executor={executor} onOpen={onOpen} />)}
    </div>
  );
}

/**
 * One row of the one list. A hand-over is ticked Done or Skipped by the planner; every other row clears by itself when
 * the plan changes (its "clears" line says how), so it has no tick.
 */
function OwnRow({ clientId, row, executor, onOpen }: { clientId: string; row: OwnListRow; executor: string; onOpen: (key: string) => void }) {
  const onSaveFailed = useSaveFailure();
  // Class B-idempotent: PUT sets the row's state to a fixed value (done / skipped / none); a second fire sets the same.
  const stateMutation = useMutation({
    mutationFn: async (state: OwnState | null) =>
      await apiRequest("PUT", `/api/clients/${clientId}/own-list/${row.key}`, { state }),
    onError: onSaveFailed,
  });
  const set = (state: OwnState | null) => {
    void saveSettled(stateMutation.mutateAsync(state)).then((ok) => {
      if (ok) void queryClient.invalidateQueries({ queryKey: ownListKey(clientId) });
    });
  };
  const words = (s: string) => fill(s, { executor });
  const done = row.state === "done";
  const off = row.state === "skipped";
  const struck = done || off;
  const tid = (what: string) => `home-own-${what}-${row.id}`;
  return (
    // s233 (D-233.37 point 5; board 12 v433): the tick LEFT of the label; "Open the page" and Skip are two fixed columns
    // on the same line (130px, 60px). s233 (D-233.74): "Open the page ›" on the hand-overs the board draws it on (print,
    // the recovery code page, the meeting) — each opens its page (a placeholder until built).
    <div className="grid min-h-[60px] grid-cols-1 items-center gap-x-4 gap-y-2 border-t border-warm-stone-light py-2 min-[1180px]:grid-cols-[minmax(0,1fr)_130px_60px]"
      data-testid={tid("row")}>
      <div className={`flex min-w-0 items-start gap-3 ${struck ? "opacity-55" : ""}`}>
        {row.hand && (
          <button type="button" role="checkbox" aria-checked={done} aria-label={`Done: ${words(row.label)}`} disabled={off}
            onClick={() => set(done ? null : "done")}
            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] border ${done ? "border-sage-600 bg-sage-600 text-warm-white" : "border-warm-stone bg-warm-white"}`}
            data-testid={tid("done")}>
            {done && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
          </button>
        )}
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className={`text-[15px] ${struck ? "line-through" : ""}`} data-testid={tid("label")}>{words(row.label)}</span>
          {row.why && <span className="text-[13px] text-warm-muted" data-testid={tid("why")}>{words(row.why)}</span>}
          {row.clears && !row.hand && <span className="text-[12.5px] text-warm-muted" data-testid={tid("clears")}>{words(row.clears)}</span>}
        </div>
      </div>
      {row.page ? (
        <div className="flex min-[1180px]:justify-end">
          <button type="button" onClick={() => onOpen(row.page)}
            className="flex min-h-11 items-center whitespace-nowrap rounded-[8px] px-2 text-[13.5px] text-sage-600 hover:bg-warm-stone-light"
            data-testid={tid("open")}>
            {HOME_BOARD.needs.act}
          </button>
        </div>
      ) : <div aria-hidden="true" className="hidden min-[1180px]:block" />}
      {row.hand && (
        <div className="flex min-[1180px]:justify-end">
          <button type="button" onClick={() => set(off ? null : "skipped")}
            className="flex min-h-11 items-center whitespace-nowrap rounded-[8px] px-2 text-[13.5px] text-sage-600 hover:bg-warm-stone-light"
            data-testid={tid("skip")}>
            {off ? HOME_BOARD.ownList.undoSkipWord : HOME_BOARD.ownList.skipWord}
          </button>
        </div>
      )}
    </div>
  );
}
